import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { TicketStatusBadge, TicketPriorityBadge } from "../../components/StatusBadge";
import { ErrorBanner, LoadingState } from "../../components/States";
import { formatDateTime } from "../../lib/format";

const STATUS_FILTERS = ["", "NEW", "PENDING", "RESOLVED", "CANCELLED"] as const;
const STATUS_FILTER_LABELS: Record<(typeof STATUS_FILTERS)[number], string> = {
  "": "All",
  NEW: "New",
  PENDING: "Pending",
  RESOLVED: "Resolved",
  CANCELLED: "Cancelled",
};

export function AdminTicketsPage() {
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>("");
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminTickets(statusFilter),
    queryFn: () => adminApiClient.listTickets({ limit: 50, status: statusFilter || undefined }),
  });

  return (
    <div className="stack">
      <div className="row row--between">
        <h1>Support tickets</h1>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="ticket-status-filter">Filter by status</label>
          <select
            id="ticket-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as (typeof STATUS_FILTERS)[number])}
          >
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>
                {STATUS_FILTER_LABELS[status]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <LoadingState label="Loading tickets…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}

      {data && (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>From</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Opened</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/admin/tickets/${t.id}`}>{t.subject}</Link>
                  </td>
                  <td>
                    {t.firstName} {t.lastName}
                  </td>
                  <td>
                    <TicketStatusBadge status={t.status} />
                  </td>
                  <td>
                    <TicketPriorityBadge priority={t.priority} />
                  </td>
                  <td>{formatDateTime(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length === 0 && <p className="muted">No tickets found.</p>}
        </div>
      )}
    </div>
  );
}
